import { Line } from "react-chartjs-2";
import AirHeardLeftIMG from "../../../assets/images/AudiogramImages/AirHeardLeft.svg";
import AirHeardRightIMG from "../../../assets/images/AudiogramImages/AirHeardRight.svg";
import AirNotHeardLeftIMG from "../../../assets/images/AudiogramImages/AirNotHeardLeft.svg";
import AirNotHeardRightIMG from "../../../assets/images/AudiogramImages/AirNotHeardRight.svg";
import BoneHeardLeftIMG from "../../../assets/images/AudiogramImages/BoneHeardLeft.svg";
import BoneHeardRightIMG from "../../../assets/images/AudiogramImages/BoneHeardRight.svg";
import BoneNotHeardLeftIMG from "../../../assets/images/AudiogramImages/BoneNotHeardLeft.svg";
import BoneNotHeardRightIMG from "../../../assets/images/AudiogramImages/BoneNotHeardRight.svg";
import { useEffect, useState } from "react";
import ucl_left from "../../../assets/images/ucl_left.svg";
import ucl_right from "../../../assets/images/ucl_right.svg";
import mcl_right from "../../../assets/images/mcl_right.svg";
import mcl_left from "../../../assets/images/mcl_left.svg";

import ucl_nh_left from "../../../assets/images/ucl-left-not-heard.svg";
import ucl_nh_right from "../../../assets/images/ucl-right-not-heard.svg";
import mcl_nh_left from "../../../assets/images/mcl-left-not-heard.svg";
import mcl_nh_right from "../../../assets/images/mcl-right-not-heard.svg";

const dottedGridPlugin = {
  id: "dottedGridPlugin",
  beforeDatasetsDraw(chart, args, pluginOptions) {
    const { ctx, chartArea, scales } = chart;
    if (!chartArea) return;

    const xScale = scales.x;
    const yScale = scales.y;
    const xTargets = (pluginOptions && pluginOptions.xTargets) || [
      "750",
      "1.5k",
      "3k",
      "6k",
    ];

    ctx.save();

    ctx.setLineDash([4, 4]);
    ctx.lineWidth = 1;
    ctx.strokeStyle = (pluginOptions && pluginOptions.xColor) || "#999";

    xTargets.forEach((lbl) => {
      const idx = xScale.ticks.findIndex((t) => t.label === lbl);
      if (idx !== -1) {
        const x = xScale.getPixelForTick(idx);
        ctx.beginPath();
        ctx.moveTo(x, chartArea.top);
        ctx.lineTo(x, chartArea.bottom);
        ctx.stroke();
      }
    });

    ctx.setLineDash([4, 4]);
    ctx.strokeStyle = (pluginOptions && pluginOptions.yColor) || "#bbb";

    ctx.restore();
  },
};

const useLoadedImage = (src, width, height) => {
  const [img, setImg] = useState(null);

  useEffect(() => {
    const image = new Image();
    image.src = src;
    image.width = width;
    image.height = height;
    image.onload = () => setImg(image);
  }, [src, width, height]);

  return img;
};

export const OscillaChartComponent = ({
  rightEar,
  heardUCL,
  heardMCL,
  n_heardUCL,
  n_heardMCL,
  heardAC,
  heardBC,
  n_heardAC,
  n_heardBC,
  oscillaMode,
  onValueChange,
  device,
  receiver,
}) => {
  let upperBoundData = [];
  let lowerBoundData = [];

  const labels = [
    "125",
    "250",
    "500",
    "750",
    "1k",
    "1.5k",
    "2k",
    "3k",
    "4k",
    "6k",
    "8k",
  ];

  const BoneHeardIMG = useLoadedImage(
    rightEar ? BoneHeardRightIMG : BoneHeardLeftIMG,
    12,
    12,
  );
  const BoneNotHeardIMG = useLoadedImage(
    rightEar ? BoneNotHeardRightIMG : BoneNotHeardLeftIMG,
    22,
    22,
  );
  const AirHeardIMG = useLoadedImage(
    rightEar ? AirHeardRightIMG : AirHeardLeftIMG,
    12,
    12,
  );
  const AirNotHeardIMG = useLoadedImage(
    rightEar ? AirNotHeardRightIMG : AirNotHeardLeftIMG,
    16,
    16,
  );
  const UCLHeardIMG = useLoadedImage(rightEar ? ucl_right : ucl_left, 12, 12);
  const UCLNotHeardIMG = useLoadedImage(
    rightEar ? ucl_nh_right : ucl_nh_left,
    22,
    22,
  );
  const MCLHeardIMG = useLoadedImage(rightEar ? mcl_right : mcl_left, 12, 12);
  const MCLNotHeardIMG = useLoadedImage(
    rightEar ? mcl_nh_right : mcl_nh_left,
    16,
    16,
  );

  const filterPoints = (labels, data) => {
    const points = [];
    labels.forEach((label, idx) => {
      if (data && data[idx] != null && data[idx] !== undefined) {
        points.push({ x: label, y: Number(data[idx]) });
      }
    });
    return points;
  };

  const splitConsecutiveSegments = (labels, points, notHeardLabels) => {
    const segments = [];
    let currentSeg = [];
    points.forEach((pt, idx) => {
      const labelIdx = labels.indexOf(pt.x);
      if (
        currentSeg.length > 0 &&
        labels
          .slice(
            labels.indexOf(currentSeg[currentSeg.length - 1].x) + 1,
            labelIdx,
          )
          .some((l) => notHeardLabels.has(l))
      ) {
        segments.push([...currentSeg]);
        currentSeg = [pt];
      } else {
        currentSeg.push(pt);
      }
    });
    if (currentSeg.length) segments.push(currentSeg);
    return segments;
  };

  const heardACPoints = filterPoints(labels, heardAC);
  const nHeardACPoints = filterPoints(labels, n_heardAC);
  const heardBCPoints = filterPoints(labels, heardBC);
  const nHeardBCPoints = filterPoints(labels, n_heardBC);

  const heardUCLPoints = filterPoints(labels, heardUCL);
  const nHeardUCLPoints = filterPoints(labels, n_heardUCL);
  const heardMCLPoints = filterPoints(labels, heardMCL);
  const nHeardMCLPoints = filterPoints(labels, n_heardMCL);

  const nHeardUCLLabels = new Set(nHeardUCLPoints.map((p) => p.x));
  const nHeardMCLLabels = new Set(nHeardMCLPoints.map((p) => p.x));

  const nHeardACLabels = new Set(nHeardACPoints.map((p) => p.x));
  const nHeardBCLabels = new Set(nHeardBCPoints.map((p) => p.x));

  const heardACSegments = splitConsecutiveSegments(
    labels,
    heardACPoints,
    nHeardACLabels,
  );
  const heardBCSegments = splitConsecutiveSegments(
    labels,
    heardBCPoints,
    nHeardBCLabels,
  );

  const heardUCLSegments = splitConsecutiveSegments(
    labels,
    heardUCLPoints,
    nHeardUCLLabels,
  );
  const heardMCLSegments = splitConsecutiveSegments(
    labels,
    heardMCLPoints,
    nHeardMCLLabels,
  );

  const datasets = [];
  heardACSegments.forEach((seg) => {
    datasets.push({
      label: "Fitting Range AC heard",
      data: seg,
      parsing: false,
      pointBackgroundColor: "white",
      pointStyle: AirHeardIMG,
      borderColor: rightEar ? "#E62323" : "#1265A1",
      radius: rightEar ? 4 : 7,
      showLine: true,
    });
  });

  nHeardACPoints.forEach((pt) => {
    datasets.push({
      label: "Fitting Range AC Not heard",
      data: [pt],
      parsing: false,
      pointBackgroundColor: "white",
      pointStyle: AirNotHeardIMG,
      borderColor: rightEar ? "#E62323" : "#1265A1",
      radius: rightEar ? 4 : 7,
      showLine: false,
    });
  });
  // For heard BC, join only consecutive segments
  heardBCSegments.forEach((seg) => {
    datasets.push({
      label: "Fitting Range BC heard",
      data: seg,
      parsing: false,
      pointBackgroundColor: "white",
      pointStyle: BoneHeardIMG,
      borderColor: rightEar ? "#E62323" : "#1265A1",
      radius: rightEar ? 4 : 7,
      showLine: true,
      borderDash: [10, 5],
    });
  });
  nHeardBCPoints.forEach((pt) => {
    datasets.push({
      label: "Fitting Range BC Not heard",
      data: [pt],
      parsing: false,
      pointBackgroundColor: "white",
      pointStyle: BoneNotHeardIMG,
      borderColor: rightEar ? "#E62323" : "#1265A1",
      radius: rightEar ? 4 : 7,
      showLine: false,
    });
  });

  heardUCLSegments.forEach((seg) => {
    datasets.push({
      label: "Fitting Range UCL heard",
      data: seg,
      parsing: false,
      pointBackgroundColor: "white",
      pointStyle: UCLHeardIMG,
      borderColor: rightEar ? "#E62323" : "#1265A1",
      radius: rightEar ? 4 : 7,
      showLine: true,
    });
  });
  nHeardUCLPoints.forEach((pt) => {
    datasets.push({
      label: "Fitting Range UCL Not heard",
      data: [pt],
      parsing: false,
      pointBackgroundColor: "white",
      pointStyle: UCLNotHeardIMG,
      borderColor: rightEar ? "#E62323" : "#1265A1",
      radius: rightEar ? 4 : 7,
      showLine: false,
    });
  });
  heardMCLSegments.forEach((seg) => {
    datasets.push({
      label: "Fitting Range MCL heard",
      data: seg,
      parsing: false,
      pointBackgroundColor: "white",
      pointStyle: MCLHeardIMG,
      borderColor: rightEar ? "#E62323" : "#1265A1",
      radius: rightEar ? 4 : 7,
      showLine: true,
    });
  });
  nHeardMCLPoints.forEach((pt) => {
    datasets.push({
      label: "Fitting Range MCL Not heard",
      data: [pt],
      parsing: false,
      pointBackgroundColor: "white",
      pointStyle: MCLNotHeardIMG,
      borderColor: rightEar ? "#E62323" : "#1265A1",
      radius: rightEar ? 4 : 7,
      showLine: false,
    });
  });

  if (device) {
    datasets.push({
      label: "Grey Area Upper Bound",
      data: upperBoundData,
      borderColor: "rgba(0, 0, 0, 0)",
      backgroundColor: "rgba(200, 200, 200, 0.5)",
      pointRadius: 0,
      fill: "+1",
    });
    datasets.push({
      label: "Grey Area Lower Bound",
      data: lowerBoundData,
      borderColor: "rgba(0, 0, 0, 0)",
      backgroundColor: "rgba(200, 200, 200, 0)",
      pointRadius: 0,
      fill: "origin",
    });
  }

  const dataFields = {
    labels,
    datasets,
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        grid: {
          display: true,
          lineWidth: 1,
          color: (ctx) => {
            const lbl = ctx.tick && ctx.tick.label;
            if (["750", "1.5k", "3k", "6k"].includes(lbl)) return "transparent";
            return "#ccc";
          },
        },
        ticks: {
          font: {
            size: 10,
            color: "#666666",
          },
        },
      },
      y: {
        title: {
          display: true,
          text: "Threshold",
          color: "#3C508B",
          font: {
            size: 10,
          },
        },
        min: -10,
        max: 120,
        reverse: "true",
        ticks: {
          callback: (value) => value,
          font: {
            size: 10,
          },
          stepSize: 10,
        },
      },
    },

    ...(onValueChange && {
      onClick: (e, ele, chart) => {
        const xValue = chart.scales.x.getValueForPixel(e.x);
        const yValue = chart.scales.y.getValueForPixel(e.y);
        const nearestIndex = Math.round(xValue);

        if (nearestIndex >= 0 && nearestIndex < labels.length) {
          const newValue = Math.round(yValue / 5) * 5;

          if (newValue >= 0 && newValue <= 120) {
            onValueChange(nearestIndex, newValue, oscillaMode);
          } else if (newValue < 0) {
            onValueChange(nearestIndex, 0, oscillaMode);
          } else {
            onValueChange(nearestIndex, 120, oscillaMode);
          }
        }
      },
    }),
    // onHover: (event, elements, chart) => {
    //     if (elements.length > 0) {
    //         chart.canvas.style.cursor = "pointer";
    //     } else {
    //         chart.canvas.style.cursor = "default";
    //     }
    // },
    // onLeave: (e, chart) => {
    //     chart.canvas.style.cursor = "default";
    //     const tooltip = chart.tooltip;
    //     tooltip.setActiveElements([], {});
    //     tooltip.update(true);
    //     chart.draw();
    // },
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: true,
        callbacks: {
          label: function (context) {
            let label = context.dataset.label || "";
            if (label) {
              label += ": ";
            }
            if (context.parsed.y !== null) {
              label += context.parsed.y === 0 ? 0 : context.parsed.y;
            }
            return label;
          },
        },
      },
      datalabels: { display: false },
    },
    animation: false,
    elements: {
      line: { borderWidth: 1 },
    },
  };
  return (
    <Line data={dataFields} options={options} plugins={[dottedGridPlugin]} />
  );
};
